import { defineStore } from 'pinia';
import type { ToastItem, ToastOptions, ToastPersistStrategy } from '@/types/toastTypes';

interface ToastState {
  immediateToasts: ToastItem[];
  queuedForNextLoad: ToastItem[];
}

const createId = () => `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeOptions = (options: ToastOptions, persist: ToastPersistStrategy): ToastItem => {
  return {
    id: options.id ?? createId(),
    title: options.title,
    description: options.description,
    type: options.type ?? 'info',
    closable: options.closable ?? true,
    autoClose: options.autoClose ?? 10000,
    position: options.position ?? 'top-right',
    draggable: options.draggable ?? true,
    animation: options.animation ?? 'zoom',
    iconClass: options.iconClass,
    persist,
  };
};

export const useToastStore = defineStore('toast', {
  state: (): ToastState => ({
    immediateToasts: [],
    queuedForNextLoad: [],
  }),
  persist: {
    pick: ['queuedForNextLoad'],
  },
  actions: {
    show(options: ToastOptions): string {
      const persist: ToastPersistStrategy = options.persist ?? 'immediate';
      const toast = normalizeOptions(options, persist);

      if (persist === 'immediate') {
        const idx = this.immediateToasts.findIndex((t) => t.id === toast.id);
        if (idx === -1) {
          this.immediateToasts.push(toast);
        } else {
          this.immediateToasts[idx] = toast;
        }
      } else if (persist === 'next-load') {
        const idx = this.queuedForNextLoad.findIndex((t) => t.id === toast.id);
        if (idx === -1) {
          this.queuedForNextLoad.push(toast);
        } else {
          this.queuedForNextLoad[idx] = toast;
        }
      }

      return toast.id;
    },

    showImmediate(options: ToastOptions): string {
      return this.show({ ...options, persist: 'immediate' });
    },

    queueForNextLoad(options: ToastOptions): string {
      return this.show({ ...options, persist: 'next-load' });
    },

    consumeQueuedForNextLoad(): ToastItem[] {
      const queued = [...this.queuedForNextLoad];
      this.queuedForNextLoad = [];
      return queued;
    },

    removeById(id: string) {
      this.immediateToasts = this.immediateToasts.filter((t) => t.id !== id);
      this.queuedForNextLoad = this.queuedForNextLoad.filter((t) => t.id !== id);
    },

    clearImmediate() {
      this.immediateToasts = [];
    },

    /**
     * Clears only "transient" immediate toasts during navigation.
     * Keeps sticky toasts that are meant to persist across route changes,
     * e.g. non-closable + non-autoclosing informational banners.
     */
    clearImmediateOnNavigation() {
      this.immediateToasts = this.immediateToasts.filter((t) => t.autoClose === false && t.closable === false);
    },

    clearAll() {
      this.immediateToasts = [];
      this.queuedForNextLoad = [];
    },
  },
});

